import { useEncerrarVigencia } from '../../../api/queries/creditoDaIa'
import { Botao } from '../../../components/Botao'
import { dataHora, dataSimples } from '../../../lib/datas'
import { TEXTOS } from '../../../lib/glossario'
import type { CreditoDeIa, PrecoDoToken } from '../../../types/creditoDaIa'
import { DIAS_PARA_CAMBIO_VELHO, emMoeda, idadeDoCambio } from './dinheiro'

interface Props {
  precos: PrecoDoToken[]
  creditos: CreditoDeIa[]
  hoje: string
  aoRevogar: (creditoId: number) => void
}

/**
 * A linha do tempo de preço e o histórico de créditos.
 *
 * **Os dois são append-only, e a tela mostra isso em vez de escondê-lo.** Preço encerrado e crédito
 * revogado continuam listados: é o que responde "quanto havia no dia 18?" e "por que a análise parou
 * naquele dia?". Não há botão de editar em lugar nenhum desta tabela — só encerrar e revogar.
 *
 * @param props os preços, os créditos, a data de hoje e o que fazer ao revogar
 * @returns as duas tabelas
 */
export function TabelaDePrecos({ precos, creditos, hoje, aoRevogar }: Props) {
  const t = TEXTOS.creditoDaIa
  const encerrar = useEncerrarVigencia()

  return (
    <div className="grid gap-5 lg:grid-cols-2">
      <section className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
        <h2 className="text-base font-semibold text-slate-900">{t.preco.titulo}</h2>
        {precos.length === 0 && <p className="mt-2 max-w-prose text-sm text-slate-600">{t.preco.semPrecos}</p>}
        <ul className="mt-3 space-y-3">
          {precos.map((preco) => {
            const dias = idadeDoCambio(preco, hoje)
            // `!preco.vigenciaFim` e não `=== null`: o servidor roda com
            // `default-property-inclusion: non_null`, então campo nulo NÃO CHEGA no JSON — ele
            // chega ausente. Comparar com `null` dava "Encerrado" no preço que estava em vigor.
            const vigente = !preco.vigenciaFim
            return (
              <li key={preco.id} className="rounded-lg border border-slate-100 bg-slate-50/60 p-3 text-sm">
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <span className="font-medium tabular-nums text-slate-900">
                    {preco.moedaDeCobranca} {preco.precoEntradaPorMilhao} / {preco.precoSaidaPorMilhao} por milhão
                  </span>
                  <span className={vigente ? 'text-xs font-medium text-green-800' : 'text-xs text-slate-500'}>
                    {vigente ? t.preco.vigente : t.preco.encerrado}
                  </span>
                </div>
                {/* `dataHora` e não a formatação de data pura: `vigenciaInicio` e `vigenciaFim` são
                    INSTANTES. Um preço encerrado às 22h de 27/08 (= 28/08T01:00Z) ficava escrito
                    como "até 28/08/2026", e esta é a peça que responde "qual preço governou as
                    chamadas do dia X" na conferência contra a fatura da Azure — um dia a mais aqui
                    move a fronteira entre dois preços. A hora fica à vista de propósito: a vigência
                    vira no instante, não à meia-noite. */}
                <p className="mt-1 text-xs text-slate-600">
                  Vale de {dataHora(preco.vigenciaInicio)}
                  {preco.vigenciaFim ? ` até ${dataHora(preco.vigenciaFim)}` : ' em diante'} · {preco.precoFonte} ·{' '}
                  {preco.modeloDeReferencia}
                  {preco.registradoPorNome ? ` · declarado por ${preco.registradoPorNome}` : ''}
                </p>
                {preco.cambio && preco.cambioEm && (
                  <p className="mt-0.5 text-xs text-slate-600">
                    {/* `cambioEm` É data pura (`LocalDate` no servidor): aqui o recorte textual é
                        o certo, e converter fuso introduziria o defeito espelhado — um dia a menos. */}
                    Câmbio {emMoeda(preco.cambio)} · {preco.cambioFonte} de {dataSimples(preco.cambioEm)}
                  </p>
                )}
                {vigente && dias !== null && dias > DIAS_PARA_CAMBIO_VELHO && (
                  <p className="mt-1 max-w-prose text-xs text-amber-800">{t.preco.cambioAntigo(dias)}</p>
                )}
                {vigente && (
                  <Botao
                    aparencia="texto"
                    tamanho="pequeno"
                    className="mt-1"
                    disabled={encerrar.isPending}
                    onClick={() => encerrar.mutate({ id: preco.id })}
                  >
                    {t.preco.encerrar}
                  </Botao>
                )}
              </li>
            )
          })}
        </ul>
        <p className="mt-3 max-w-prose text-xs text-slate-500">{t.preco.naoEdita}</p>
      </section>

      <section className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
        <h2 className="text-base font-semibold text-slate-900">{t.credito.historico}</h2>
        {creditos.length === 0 && <p className="mt-2 text-sm text-slate-600">{t.credito.semCreditos}</p>}
        <ul className="mt-3 space-y-3">
          {creditos.map((credito) => (
            <li key={credito.id} className="rounded-lg border border-slate-100 bg-slate-50/60 p-3 text-sm">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <span className="font-medium tabular-nums text-slate-900">
                  {credito.competencia} — {emMoeda(credito.valor)}
                </span>
                {credito.revogadoEm ? (
                  <span className="text-xs text-slate-500">{t.credito.revogado}</span>
                ) : (
                  <Botao aparencia="texto" tamanho="pequeno" onClick={() => aoRevogar(credito.id)}>
                    {t.credito.revogar}
                  </Botao>
                )}
              </div>
              <p className="mt-1 text-xs text-slate-600">
                {credito.motivo}
                {credito.registradoPorNome ? ` — ${credito.registradoPorNome}` : ''},{' '}
                {/* Instante: quando o crédito foi declarado. */}
                {dataHora(credito.registradoEm)}
              </p>
            </li>
          ))}
        </ul>
        <p className="mt-3 max-w-prose text-xs text-slate-500">{t.credito.aviso}</p>
      </section>
    </div>
  )
}
